import { useState } from 'react';
import {
  RiAddLine,
  RiBuilding2Line,
  RiCloseLine,
  RiDeleteBin6Line,
  RiImageLine,
  RiMapPinLine,
  RiNodeTree,
  RiUserStarLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useStore } from '../../state/store';
import { useCanvasStore } from '../../canvas/canvasStore';
import { DRAG_MIME, type DragPayload } from '../../canvas/drag';
import { RISK_BG, RISK_LABEL } from '../icons';
import { PRIORITY_LABEL, TARGET_STATUS_LABEL, type Priority } from '../../state/domain';
import { cn, formatDay } from '@/lib/utils';

function dragPayload(target: {
  id: string;
  name: string;
  codename: string | null;
  role: string | null;
  org: string | null;
  location: string | null;
  riskScore: number;
  riskLevel: string;
  summary: string | null;
  photo: string | null;
}): DragPayload {
  return {
    kind: 'target',
    id: target.id,
    title: target.codename ? `${target.name}（${target.codename}）` : target.name,
    text: [
      target.role ? `职务：${target.role}` : '',
      target.org ? `组织：${target.org}` : '',
      target.location ? `位置：${target.location}` : '',
      `风险：${target.riskScore}（${target.riskLevel}）`,
      target.summary ?? '',
    ]
      .filter(Boolean)
      .join('\n'),
    color:
      target.riskLevel === 'high' || target.riskLevel === 'critical'
        ? 'red'
        : target.riskLevel === 'medium'
          ? 'yellow'
          : 'blue',
    photo: target.photo ?? undefined,
  };
}

export function TargetsPanel() {
  const targets = useStore(state => state.targets);
  const activeTargetId = useStore(state => state.activeTargetId);
  const selectTarget = useStore(state => state.selectTarget);
  const createTarget = useStore(state => state.createTarget);
  const deleteTarget = useStore(state => state.deleteTarget);
  const controller = useCanvasStore(state => state.controller);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    codename: '',
    kind: 'person' as 'person' | 'organization',
    role: '',
    org: '',
    location: '',
    nationality: '',
    dob: '',
    photo: '',
    affiliations: '',
    priority: 'normal' as Priority,
    summary: '',
    tags: '',
  });

  const emptyForm = {
    name: '',
    codename: '',
    kind: 'person' as 'person' | 'organization',
    role: '',
    org: '',
    location: '',
    nationality: '',
    dob: '',
    photo: '',
    affiliations: '',
    priority: 'normal' as Priority,
    summary: '',
    tags: '',
  };

  const submit = async () => {
    if (!form.name.trim()) return;
    await createTarget({
      name: form.name.trim(),
      kind: form.kind,
      codename: form.codename || undefined,
      role: form.role || undefined,
      org: form.org || undefined,
      location: form.location || undefined,
      nationality: form.nationality || undefined,
      dob: form.dob || undefined,
      photo: form.photo || undefined,
      priority: form.priority,
      affiliations: form.affiliations
        .split(/[,，\s]+/)
        .map(t => t.trim())
        .filter(Boolean),
      summary: form.summary || undefined,
      tags: form.tags
        .split(/[,，\s]+/)
        .map(t => t.trim())
        .filter(Boolean),
    });
    setForm(emptyForm);
    setCreating(false);
  };

  const placeOnBoard = (targetId: string) => {
    if (!controller) return;
    const target = targets.find(t => t.id === targetId);
    if (!target) return;
    const center = controller.viewport.center;
    controller.addEntityNode([center[0], center[1] - 120], {
      title: target.name,
      text: [
        `类型：${target.kind === 'organization' ? '组织' : '个人'}`,
        target.role ? `职务：${target.role}` : '',
        target.org ? `组织：${target.org}` : '',
        `风险：${target.riskScore}（${RISK_LABEL[target.riskLevel] ?? target.riskLevel}）`,
        target.summary ?? '',
      ]
        .filter(Boolean)
        .join('\n'),
      color:
        target.riskLevel === 'high' || target.riskLevel === 'critical'
          ? 'red'
          : target.riskLevel === 'medium'
            ? 'yellow'
            : 'blue',
      refKind: 'target',
      refId: target.id,
    });
    useStore.getState().toast('success', '已放置到关联图');
  };

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">对象档案</span>
        <Button size="xs" variant={creating ? 'ghost' : 'outline'} onClick={() => setCreating(v => !v)}>
          {creating ? <RiCloseLine className="size-3" /> : <RiAddLine className="size-3" />}
          {creating ? '取消' : '新增对象'}
        </Button>
      </div>

      {creating && (
        <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-border bg-muted/40 p-2.5">
          <div className="flex gap-1">
            {(['person', 'organization'] as const).map(kind => (
              <button
                key={kind}
                type="button"
                onClick={() => setForm({ ...form, kind })}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[11px] transition-colors',
                  form.kind === kind
                    ? 'border-primary bg-primary/8 text-primary'
                    : 'border-border bg-card text-muted-foreground hover:bg-accent'
                )}
              >
                {kind === 'person' ? <RiUserStarLine className="size-3.5" /> : <RiBuilding2Line className="size-3.5" />}
                {kind === 'person' ? '个人' : '组织'}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>姓名 / 名称 *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>代号</Label>
              <Input value={form.codename} onChange={e => setForm({ ...form, codename: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label>头像 / 照片</Label>
            <div className="flex items-center gap-2">
              <div className="grid size-11 place-items-center overflow-hidden rounded-[var(--radius-sm)] border border-border bg-muted">
                {form.photo ? (
                  <img src={form.photo} alt="" className="size-full object-cover" />
                ) : (
                  <RiImageLine className="size-4 text-muted-foreground" />
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setForm(current => ({ ...current, photo: String(reader.result ?? '') }));
                    reader.readAsDataURL(file);
                  }}
                />
                <span className="inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-sm)] border border-input bg-card px-2.5 text-[12px] hover:bg-accent">
                  <RiImageLine className="size-3.5" />
                  上传
                </span>
              </label>
              {form.photo && (
                <Button size="xs" variant="ghost" onClick={() => setForm({ ...form, photo: '' })}>
                  清除
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>职务 / 角色</Label>
              <Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>所属组织</Label>
              <Input value={form.org} onChange={e => setForm({ ...form, org: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>所在地</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>国籍</Label>
              <Input value={form.nationality} onChange={e => setForm({ ...form, nationality: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>出生日期</Label>
              <Input type="date" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>优先级</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value as Priority })}
              >
                {(Object.keys(PRIORITY_LABEL) as Priority[]).map(level => (
                  <option key={level} value={level}>
                    {PRIORITY_LABEL[level]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label>所属机构（逗号分隔）</Label>
            <Input value={form.affiliations} onChange={e => setForm({ ...form, affiliations: e.target.value })} />
          </div>

          <Label>背景备注</Label>
          <Textarea rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} />
          <Label>标签（逗号分隔）</Label>
          <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
          <Button size="sm" onClick={submit}>
            建档
          </Button>
        </div>
      )}

      {targets.length === 0 && !creating && (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground">
          尚无对象，先建立档案。
        </div>
      )}

      <div className="flex flex-col gap-2">
        {targets.map(target => (
          <div
            key={target.id}
            onClick={() => selectTarget(target.id)}
            draggable
            onDragStart={event => {
              event.dataTransfer.setData(DRAG_MIME, JSON.stringify(dragPayload(target)));
              event.dataTransfer.effectAllowed = 'copy';
            }}
            title="拖到关联墙上（或点“上板”）"
            className={cn(
              'group cursor-grab rounded-[var(--radius)] border bg-card p-2.5 transition-colors active:cursor-grabbing',
              activeTargetId === target.id
                ? 'border-primary/60 ring-1 ring-primary/20'
                : 'border-border hover:border-input'
            )}
          >
            <div className="flex items-start gap-2">
              <div
                className={cn(
                  'mt-0.5 grid size-9 shrink-0 place-items-center overflow-hidden rounded-[5px]',
                  target.kind === 'organization' ? 'bg-secondary' : 'bg-muted'
                )}
              >
                {target.photo ? (
                  <img src={target.photo} alt="" className="size-full object-cover" />
                ) : target.kind === 'organization' ? (
                  <RiBuilding2Line className="size-3.5 text-secondary-foreground" />
                ) : (
                  <RiUserStarLine className="size-3.5 text-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[12.5px] font-semibold">
                    {target.name}
                    {target.codename && (
                      <span className="ml-1 font-normal text-[10.5px] text-muted-foreground">代号 {target.codename}</span>
                    )}
                  </span>
                  <Badge variant="outline" className={cn('shrink-0 border-transparent', RISK_BG[target.riskLevel])}>
                    {target.riskScore} {RISK_LABEL[target.riskLevel]}
                  </Badge>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] text-muted-foreground">
                  {target.priority !== 'normal' && (
                    <span className="text-[var(--color-cord)]">优先级 {PRIORITY_LABEL[target.priority]}</span>
                  )}
                  <span>{TARGET_STATUS_LABEL[target.status]}</span>
                  {target.role && <span>{target.role}</span>}
                  {target.org && <span>{target.org}</span>}
                  {target.location && (
                    <span className="inline-flex items-center gap-0.5">
                      <RiMapPinLine className="size-3" />
                      {target.location}
                    </span>
                  )}
                </div>
                {target.tags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {target.tags.map(tag => (
                      <Badge key={tag} variant="muted">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
              <span className="text-[10px] text-muted-foreground">建档 {formatDay(target.createdAt)}</span>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={e => {
                    e.stopPropagation();
                    placeOnBoard(target.id);
                  }}
                >
                  <RiNodeTree className="size-3" />
                  上板
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={e => {
                    e.stopPropagation();
                    if (confirm(`确认删除对象「${target.name}」？`)) deleteTarget(target.id);
                  }}
                >
                  <RiDeleteBin6Line className="size-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}